import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ProducerFormData } from '../types';

interface ContactSectionProps {
  formData: ProducerFormData;
  updateField: <K extends keyof ProducerFormData>(key: K, value: ProducerFormData[K]) => void;
}

function ContactField({
  id,
  label,
  value,
  onChange,
  placeholder,
  type,
  isPublic,
  onPublicChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  type?: string;
  isPublic: boolean;
  onPublicChange: (v: boolean) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      <div className="flex items-center gap-2">
        <Switch
          id={`${id}-public`}
          checked={isPublic}
          onCheckedChange={onPublicChange}
          className="scale-90"
        />
        <Label htmlFor={`${id}-public`} className="text-xs text-muted-foreground font-normal cursor-pointer">
          Visa publikt
        </Label>
      </div>
    </div>
  );
}

export function ContactSection({ formData, updateField }: ContactSectionProps) {
  return (
    <div className="space-y-8">
      {/* Contact fields */}
      <div>
        <h3 className="text-lg font-medium text-foreground">Kontaktuppgifter</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Hur kan besökare nå er? Välj vilka uppgifter som visas publikt.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
        <ContactField
          id="phone"
          label="Telefon"
          value={formData.phone}
          onChange={(v) => updateField('phone', v)}
          placeholder="070-123 45 67"
          isPublic={formData.phonePublic}
          onPublicChange={(v) => updateField('phonePublic', v)}
        />

        <ContactField
          id="email"
          label="E-post"
          type="email"
          value={formData.email}
          onChange={(v) => updateField('email', v)}
          placeholder="hej@gard.se"
          isPublic={formData.emailPublic}
          onPublicChange={(v) => updateField('emailPublic', v)}
        />

        <ContactField
          id="website"
          label="Webbplats"
          value={formData.website}
          onChange={(v) => updateField('website', v)}
          placeholder="https://gard.se"
          isPublic={formData.websitePublic}
          onPublicChange={(v) => updateField('websitePublic', v)}
        />

        <ContactField
          id="facebook"
          label="Facebook"
          value={formData.facebook}
          onChange={(v) => updateField('facebook', v)}
          placeholder="https://facebook.com/gard"
          isPublic={formData.facebookPublic}
          onPublicChange={(v) => updateField('facebookPublic', v)}
        />

        <ContactField
          id="instagram"
          label="Instagram"
          value={formData.instagram}
          onChange={(v) => updateField('instagram', v)}
          placeholder="@gard"
          isPublic={formData.instagramPublic}
          onPublicChange={(v) => updateField('instagramPublic', v)}
        />

        <div className="md:col-span-2">
          <Label htmlFor="openingHoursText">Besöksöppet</Label>
          <Textarea
            id="openingHoursText"
            value={formData.openingHoursText}
            onChange={(e) => updateField('openingHoursText', e.target.value)}
            placeholder="När kan man komma förbi? T.ex. 'Lör–sön 11–16' eller 'Enligt överenskommelse'."
            rows={2}
          />
        </div>
      </div>

    </div>
  );
}
